import { useContext, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { BoxFull, Button, TransactionalSubheader } from "iuvity-ui-components";
import {Header } from "../../components";
import {
  CustomDialog,
  DialogProps,
} from "../../components/dialog/CustomDialog";
import { PermissionsFormSchema } from "../../validations/schemas";
import { PERMISSIONS_STATUS_OPTIONS, REVALIDATE_FORM_PROPS, calendarTranslations, permissionsFieldsSelecter } from "../../constants";
import { AppContext } from "../../context/index";
import { CustomFormComponent } from "../../components/shared/CustomFormComponent";
import { useCatalogs } from "../../hooks/useCatalogs";
import { masterCategory } from "../../utils";
import { usePermissions } from "../../hooks/usePermissions";
import { useFunctionalities } from "../../hooks/useFunctionalities";
import { formatStringToDate2 } from "../../helpers/formatDate";

interface NewPermissionsProps {
  update?: boolean;
  clone?: boolean;
  detail?: boolean;
}

const permissionDefaultValues = {
  functionalityId: "",
  privilegeId: "",
  protocolId: "",
  methodId: "",
  port: "",
  pagePath: "",
  servicePath: "",
  expeditionDate: null,
  expirationDate: null,
};

export const NewPermissionPage: React.FC<NewPermissionsProps> = ({ update = false, clone = false, detail = false }) => {
  const navigate = useNavigate();
  const {
    t,
    appState: { user, selectedPermission },
  } = useContext(AppContext);

  const form = useForm({
    mode: "onChange",
    delayError: 1200,
    reValidateMode: "onChange",
    defaultValues: (update || detail) ? 
      {
        id: selectedPermission?.id,
        functionalityId: selectedPermission?.functionalityId,
        privilegeId: selectedPermission?.privilegeId,
        protocolId: selectedPermission?.protocolId,
        methodId: selectedPermission?.methodId,
        port: selectedPermission?.port,
        pagePath: selectedPermission?.pagePath,
        servicePath: selectedPermission?.servicePath,
        statusId: selectedPermission?.statusId,
        expeditionDate: detail 
                        ? formatStringToDate2(selectedPermission?.expeditionDate || '', true, t)
                        : formatStringToDate2(selectedPermission?.expeditionDate || '', false),
        expirationDate: detail 
                        ? formatStringToDate2(selectedPermission?.expirationDate || '', true, t)
                        : formatStringToDate2(selectedPermission?.expirationDate || '', false),
        updatedby: user?.name
      } : clone ? 
      {
        functionalityId: selectedPermission?.functionalityId,
        privilegetoclone: selectedPermission?.privilegeId,
        privilegeId: '',
        protocolId: selectedPermission?.protocolId,
        methodId: selectedPermission?.methodId,
        port: selectedPermission?.port,
        pagePath: selectedPermission?.pagePath,
        servicePath: selectedPermission?.servicePath,
        expeditionDate:!!selectedPermission?.expeditionDate? formatStringToDate2(selectedPermission?.expeditionDate, false) :null,
        expirationDate: !!selectedPermission?.expirationDate? formatStringToDate2(selectedPermission?.expirationDate, false): null,
        createdby: user?.name
      } : 
      {
        ...permissionDefaultValues,
        createdby: user?.name
      }
  });

  const {
    getValues,
    formState: { isValid, isDirty },
  } = form;

  const { permissionsAction } = usePermissions("ACTIONS");
  const { data: dataCatalogs } = useCatalogs("DATA");
  const { data: dataFunctionalities } = useFunctionalities("DATA");
  const [modalProps, setModalProps] = useState<DialogProps>({
    visible: false,
  } as DialogProps);

  const formKeysOptions = { 
    functionalityId: dataFunctionalities?.filter((item: any) => item.statusId === 'ACTIVE' || item.statusId === 'DRAFT')?.map((item: any) => ({label: item.code,  value: item.code})),
    privilegetoclone: masterCategory(dataCatalogs, 'PRIVILEGE_TYPES'),
    privilegeId: masterCategory(dataCatalogs, 'PRIVILEGE_TYPES'),
    protocolId: masterCategory(dataCatalogs, 'PROTOCOL_TYPES'),
    methodId: masterCategory(dataCatalogs, 'METHOD_TYPES'),
    statusId: PERMISSIONS_STATUS_OPTIONS(t)
      .sort((a, b) => a.label.localeCompare(b.label))
  };

  const optionalFieldsObject = {
    expeditionDate: true,
    expirationDate: true,
  }

  const [calendarOptions, setCalendarOptions] = useState<any>({
    expeditionDate: {
      value: undefined,
      minDate: new Date(),
      maxDate: undefined,
      disabled: false,
    },
    expirationDate: {
      value: undefined,
      minDate: new Date(),
      maxDate: undefined,
      disabled: ((update || clone) && form?.getValues('expirationDate') !== '') ? false : true,
    },
  })

  const onReturn = () => {
    navigate(-1);
    window.scrollTo(0, 0);
  };

  const onAction = async () => {
    const formValues = getValues();
    const actionStatus = await permissionsAction(update ? "UPDATE" : "CREATE", 
      update ? 
        {
          ...formValues,
        expeditionDate: getValues('expeditionDate')!=null && getValues('expeditionDate')!=''? getValues('expeditionDate').toISOString().slice(0, -5).replace('T', ' ') : null,
          expirationDate: getValues('expirationDate')!=null && getValues('expirationDate')!=''? getValues('expirationDate').toISOString().slice(0, -5).replace('T', ' ') : null,
        }
      : {
          ...formValues,
          expeditionDate: !(getValues('expeditionDate') === null || getValues('expeditionDate') === '')
            ? getValues('expeditionDate').toISOString().slice(0, -5).replace('T', ' ') : getValues('expeditionDate'),
          expirationDate: !(getValues('expirationDate') === null || getValues('expirationDate') === '')
            ? getValues('expirationDate').toISOString().slice(0, -5).replace('T', ' ') : getValues('expirationDate'),
        }
    );
    if (actionStatus === "SUCCESS") {
      navigate(-1);
      window.scrollTo(0, 0);
    }
  };

  const handleModalProps = (actionType: "action" | "cancel") => {
    switch (actionType) {
      case "action":
        return setModalProps((prev) => ({
          ...prev,
          visible: true,
          title: t(`Permissions.${update ? "save" : "create"}ModalTitle`),
          content: t(`Permissions.${update ? "save" : "create"}ModalContent`),
          onConfirm: onAction,
        }));
      case "cancel":
        return setModalProps((prev) => ({
          ...prev,
          visible: true,
          title: t("Permissions.cancelModalTitle"),
          content: t("Permissions.cancelModalContent"),
          onConfirm: onReturn,
        }));
      default:
        return;
    }
  };

  const permissionsFields = permissionsFieldsSelecter(
    update
      ? 'update'
      : clone
        ? 'clone'
        : detail 
          ? 'detail'
          : 'default'
  )

  return (
    <>
      {/* <Header
        welcomeMessage={t("Dashboard.welcomeTitle")}
        userName={user?.name}
      /> */}

      <TransactionalSubheader
        subheaderTitle={
          <div style={{ position: "relative", left: "-60px" }}>
            <i
              className="iu iu-icon-left pr-3"
              onClick={onReturn}
              aria-hidden="true"
              style={{
                fontSize: "var(--font-size-tablet-title)",
                cursor: "pointer",
              }}
            />
              <span style={{ color: "var(--cardBackgroundColorOneShade80)" }}>
                {
                  update 
                    ? t("Permissions.editPermissionsTitle") 
                    : clone 
                      ? t("Permissions.clonePermissionsTitle")
                      : detail
                        ? t("Permissions.detailPermissionsTitle")
                        : t("Permissions.createPermissionsTitle")
                }
              </span>
          </div>
        }
      >
        <TransactionalSubheader.BreadCrumb
          id={t("Permissions.permissionsTitle")}
          disabledDefaultHome={true}
          breadcrumbsHomeItem={{
            icon: "iu iu-icon-home",
            command: () => navigate("/"),
          }}
          breadcrumbsItems={[
            {
              label: t("ConsoleTranslations.adminConsoleMenuLabel"),
              disabled: true
            },
            {
                label: t("Permissions.permissionsTitle"),
                command: () => onReturn(),
                style: { fontFamily: "var(--fontBarlowSemiBold)" },
            },
            {
              label: update
              ? t("Permissions.editPermissionsBreadcrumb")
              : clone
                ? t("Permissions.clonePermissionsBreadcrumb")
                : detail
                  ? t("Permissions.detailPermissionsBreadcrumb")
                  : t("Permissions.createPermissionsBreadcrumb"),
              disabled: true
            },
          ]}
        />
        <TransactionalSubheader.Title />
      </TransactionalSubheader>
      
      <div id='permissions-customformcomponent'>
        <CustomFormComponent 
          functionality="Permissions"
          fields={permissionsFields}
          form={form}
          schemaOfValidation={PermissionsFormSchema}
          dropdownOptions={formKeysOptions}
          boxTitle={
            update 
              ?
                t('Permissions.editPermissionsDescription') 
              : clone
                ? t('Permissions.clonePermissionsDescription')
                : detail
                  ? ''
                  : t('Permissions.createPermissionsDescription')
          }
          isOptional={optionalFieldsObject}
          language={localStorage.getItem('LANGUAGE') || 'en'}
          calendarTranslations={calendarTranslations}
          calendarOptions={calendarOptions}
          setCalendarOptions={setCalendarOptions}
          REVALIDATE_FORM_PROPS={REVALIDATE_FORM_PROPS}
          readOnlyObject={{
            functionalityId: detail ? true : false,
            privilegeId: detail ? true : false,
            privilegetoclone: clone ? true : false,
            protocolId: detail ? true : false,
            methodId: detail ? true : false,
            port: detail ? true : false,
            pagePath: detail ? true : false,
            servicePath: detail ? true : false,
            statusId: detail ? true : false,
            expeditionDate: detail ? true : false,
            expirationDate: detail ? true : false,
          }}
        />
      </div>

      <BoxFull className="pt-0 px-4 pb-5" style={{ backgroundColor: "transparent" }}>
        <div className="flex align-items-center justify-content-end gap-5">
          {!detail && 
            <Button
              variant="p-button-link"
              label={t("Permissions.cancelBtnLabel")}
              onClick={isDirty ? () => handleModalProps("cancel") : onReturn}
            />
          }
          <Button
            variant="p-button"
            label={
              update 
                ? t("Permissions.saveBtnLabel")
                : clone
                  ? t("Permissions.cloneBtnLabel")
                  : detail
                    ? t("Permissions.goBackBtnLabel")
                    : t("Permissions.createBtnLabel")
            }
            disabled={update || clone ? !(isValid && isDirty) : !isValid}
            onClick={detail ? onReturn : () => handleModalProps("action")}
          />
        </div>
      </BoxFull>

      <CustomDialog
        title={modalProps.title}
        content={modalProps.content}
        visible={modalProps.visible}
        handleModalState={setModalProps}
        onConfirm={modalProps.onConfirm}
      />
    </>
  );
};
